
<template>
  <div class="dropdown">
    <SearchBarInput
      id="searchBarInput"
      @update-search-term="updateSearchTerm"
      @click="shouldDisplayResults = true"
      @handle-keys="handleKeys"
    />
    <div
      id="dropdown-menu"
      class="dropdown-menu"
      role="menu"
    >
      <div
        v-if="shouldDisplayResults && searchTerm"
        ref="dropdownContent"
        class="dropdown-content"
      >
        <SearchResult
          v-for="(result, index) in totalResults"
          :key="index"
          ref="searchResult"
          :result="result"
          class="dropdown-item"
          :class="computeStyle(result, index)"
        />
        <div
          v-if="loading && totalResults.length === 0"
          class="result-misc"
        >
          <LoadIcon />
        </div>
        <p
          v-else-if="totalResults.length === 0 && !loading"
          class="dropdown-item result-misc"
        >
          No results found
        </p>
      </div>
    </div>
  </div>
</template>

<script>
// Services and Helpers
import {gmAPI, MapService} from '@/services/MapService';
import {markRaw} from 'vue';
import {mapActions} from 'vuex';

// Components
import SearchBarInput from './sections/SearchBarInput.vue';
import SearchResult from './sections/SearchResult.vue';
import LoadIcon from '@/components/svgs/LoadIcon.vue';

let timer;
export default {
  name: 'SearchBar',
  components: {
    SearchBarInput,
    SearchResult,
    LoadIcon,
  },
  props: {
    searchResults: {
      default: function() {
        return [];
      },
      type: Array,
    },
    shouldCollapseMenu: {
      default: false,
      type: Boolean,
    },
  },
  emits: [
    'updateSearchTerm',
    'updateShouldCollapseMenu',
  ],
  data() {
    return {
      googleSearchIndex: {
        ref: 'googleResults',
        results: [],
        numResults: 6,
        /** @type {google.maps.InfoWindow} */
        infoWindow: new gmAPI.InfoWindow(),
        marker: null,
      },
      map: null,
      autocompleteService: null,
      placesService: null,
      focused: -1,
      selected: -1,
      loading: false,
      shouldDisplayResults: true,
      searchTerm: '',
    };
  },
  computed: {
    totalResults() {
      return this.searchResults.concat(this.googleSearchIndex.results);
    },
  },
  watch: {
    searchTerm(newValue) {
      // If entire search term is deleted, reset focused index
      if (!newValue) {
        this.focused = -1;
      }
    },
    shouldCollapseMenu(newValue) {
      if (newValue) {
        this.collapseMenu();
        this.$emit('updateShouldCollapseMenu', false);
      }
    },
  },
  mounted() {
    // Initialize the autocomplete and places services
    this.map = MapService.getMap();
    this.autocompleteService = new gmAPI.places.AutocompleteService();
    this.placesService = new gmAPI.places.PlacesService(this.map);
  },
  unmounted() {
    // If a google result was selected, close out the infowindow and remove the marker from the map
    const googleIndex = this.googleSearchIndex;
    googleIndex.infoWindow.close();
    if (googleIndex.marker) {
      googleIndex.marker.setMap(null);
    }
  },
  methods: {
    /**
     * @param {Object} result
     * @param {number} index
     * @return {string} output - the class name to be applied to the SearchResult component
     */
    computeStyle(result, index) {
      let output = result.customOptions.style ?? '';
      if (index === this.focused) {
        output += ' focusedResult';
      }
      return output;
    },
    ...mapActions(['addErrorMessage']),
    /**
     * Handles traversing the total results array when using arrow keys to navigate
     * @param {string} code - The Event's code emitted from SearchBarInput component
     */
    handleKeys(code) {
      // Empty return if no results returned
      if (!this.totalResults.length) return;
      // up arrow
      if (code === 'ArrowUp') {
        // Keying through results list logic
        if (this.focused <= 0) {
          this.focused = this.totalResults.length - 1;
        } else {
          this.focused--;
        }
      }
      // down arrow
      if (code === 'ArrowDown') {
        // Keying through results list logic
        if (this.focused === this.totalResults.length - 1) {
          this.focused = 0;
        } else {
          this.focused++;
        }
      }
      // If the inside div needs to be scrolled to show the focused SearchResult on keypress, do so
      if (
        // Need to scroll down
        this.$refs.dropdownContent.clientHeight < this.$refs.searchResult[this.focused].$el.offsetTop ||
        // Need to scroll up - 55 represents the search input height and its margin-bottom, and the margin-top of
        // the dropdown content
        this.$refs.searchResult[this.focused].$el.offsetTop - 55 < this.$refs.dropdownContent.scrollTop
      ) {
        this.$refs.searchResult[this.focused].$el.scrollIntoView();
      }
      // enter key
      if (code === 'Enter' && this.focused >= 0) {
        // Remove focus from search bar input
        document.activeElement.blur();
        // Trigger the appropriate search result's click handler
        this.$refs.searchResult[this.focused].handleClick();
        // reset the index
        this.focused = -1;
      }
    },
    updateSearchTerm(searchTerm) {
      // Update the search term for the parent component
      this.$emit('updateSearchTerm', searchTerm);
      // Update local searchTerm
      this.searchTerm = searchTerm;
      this.loading = true;
      const updateQueryPredictions = this.debounce(() =>
        this.getQueryPredictions(searchTerm),
      );
      updateQueryPredictions();
    },
    debounce(func, timeout = 250) {
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    getQueryPredictions(searchTerm) {
      if (searchTerm) {
        this.autocompleteService.getQueryPredictions(
          {input: `${searchTerm}`},
          this.renderGoogleResults,
        );
      }
    },
    renderGoogleResults(predictions, status) {
      this.loading = false;
      if (
        status != gmAPI.places.PlacesServiceStatus.OK ||
        !predictions
      ) {
        // 'ZERO_RESULTS' is not really an error, so don't alert it
        if (status !== 'ZERO_RESULTS') {
          console.log(status);
        }
        return;
      }
      const output = predictions.splice(0, this.googleSearchIndex.numResults).map((result) => {
        // Append a standardized 'customOptions' property that we can access from the SearchResult.vue component
        result.customOptions = {
          label: [result.description],
          clickHandler: this.googleClickHandler,
          type: 'google',
        };
        return result;
      });
      // Return specified number of results from predictions
      this.googleSearchIndex.results = output;
    },
    /**
     * When a Google Places result is clicked, retrieve the place's details, create and set a marker on its location,
     * and populate an infowindow with the place's name and address.
     * @param {Object} result - The selected Google plaes result
     */
    googleClickHandler(result) {
      const googleIndex = this.googleSearchIndex;
      const map = this.map;
      // If a Google search result marker was previously created, remove it
      if (googleIndex.marker) {
        googleIndex.marker.setMap(null);
      }

      // Set new gmAPI marker
      googleIndex.marker = markRaw(new gmAPI.Marker({
        map,
        // anchorPoint: new window.google.maps.Point(0, -29),
      }));

      // If the result doesn't have a place id, we can't display it on the map so return
      if (!result.place_id) {
        this.addErrorMessage(`No place details could be found for ${result.description}`);
        return;
      }

      this.placesService.getDetails({placeId: result.place_id}, (place) => {
        if (!place.geometry || !place.geometry.location) {
        // User entered the name of a Place that was not suggested and
          // pressed the Enter key, or the Place Details request failed.
          this.addErrorMessage(`No details available for input: '${place.name}'`);
          return;
        }

        // If the place has a geometry, then present it on a map.
        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport);
        } else {
          this.map.setCenter(place.geometry.location);
          this.map.setZoom(17);
        }


        googleIndex.marker.setPosition(place.geometry.location);
        googleIndex.marker.setVisible(true);
        const content = `<div class='gp-infowindow'>
          <h2>${place.name}</h2>
          <p>${place.formatted_address}</p>
        </div>`;
        googleIndex.infoWindow.setContent(content);
        googleIndex.infoWindow.setPosition(place.geometry.location);
        googleIndex.infoWindow.open(this.map, googleIndex.marker);
        googleIndex.infoWindow.addListener('closeclick', () => {
          googleIndex.marker.setVisible(false);
        });
        this.collapseMenu();
      });
    },
    collapseMenu() {
      this.shouldDisplayResults = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown {
  display: flex;
  flex-direction: column;

  &-menu {
    // 50px is used because it is the height of the search input + the margin-bottom
    max-height: calc(100% - 50px);
  }

  &-content {
    margin-top: 5px;
    border-radius: 2px;
    padding: 0;
    text-align: left;
    overflow-x: hidden;
    scroll-behavior: smooth;
    // 5px is used to account for the margin-top of the dropdown content
    max-height: calc(100% - 5px);
  }
}

#searchBarInput {
  margin-bottom: 5px;
  height: 45px;
}

.title {
  font-size: 22px;
  font-weight: bold;
  color: main.$npGray;
}

.result-misc {
  width: 300px;
  height: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.subtitle {
  font-size: 14px;
  font-weight: normal;
  color: main.$npGray;
  padding-top: 0.5rem;
  display: block;
}

.infowindowContent {
  padding: 15px;
}

.focusedResult {
  background-color: #eae8e8;
}

.divider {
  border-bottom: solid main.$lightGray 1px;
}
</style>
